import type {
  BdAppConfigEnvironment,
  BdAppConfigRequest,
  BdAppConfigResponse,
  BdAppConfigResponseMsalConfig
} from './app-config';
import { backendHostOrigins, expandHostedEndpoints, hostProtectedResources } from './hosted-api-endpoint';

export class BdAppConfigHandler {

  public static load(
    request: BdAppConfigRequest,
    source: Record<string, unknown> = process.env
  ): BdAppConfigResponse {
    const expanded = expandHostedEndpoints(request, source);
    const hostUrl = request.hostKey ? BdAppConfigHandler._getSourceValue(request.hostKey, expanded) : null;
    const environment = BdAppConfigHandler._getSourceValue('BD_ENVIRONMENT', expanded) as BdAppConfigEnvironment;
    const apiEndpoints = BdAppConfigHandler._loadEnvironmentVariables(request.apiEndpointKeys, expanded);
    const unprotectedApiEndpoints = BdAppConfigHandler._loadEnvironmentVariables(request?.unprotectedApiEndpointKeys ?? [], expanded);
    const variables = BdAppConfigHandler._loadEnvironmentVariables(request.variableKeys, expanded);
    const allApiEndpoints = BdAppConfigHandler._mergeEndpoints(apiEndpoints, unprotectedApiEndpoints);
    const msalConfig = BdAppConfigHandler._createMsalConfig(apiEndpoints, expanded);
    const backendHosts = backendHostOrigins(expanded);

    return {
      hostUrl,
      environment,
      ...(Object.keys(backendHosts).length > 0 ? { backendHosts } : {}),
      apiEndpoints: allApiEndpoints,
      variables,
      msalConfig
    };
  }

  private static _loadEnvironmentVariables(
    apiEndpointKeys: string[],
    source: { [key: string]: unknown }
  ): { [key: string]: string } {

    // important, angular can not use static methods with function as return
    // noinspection UnnecessaryLocalVariableJS
    if (!!apiEndpointKeys && apiEndpointKeys.length > 0) {
      const mappingFn = apiEndpointKeys.reduce((previousValue, currentValue) => {
        previousValue[currentValue] = source[currentValue] as string;

        return previousValue;
      }, {} as { [key: string]: string })

      return mappingFn;
    } else {
      return {};
    }
  }

  private static _createMsalConfig(
    apiEndpoints: { [key: string]: string },
    source: { [key: string]: unknown }
  ): BdAppConfigResponseMsalConfig {
    const clientId: string = BdAppConfigHandler._getSourceValue('AUTHENTICATION_CLIENT_ID', source);
    const signUpSignInPolicy: string = BdAppConfigHandler._getSourceValue('SIGN_UP_SIGN_IN_POLICY_NAME', source);
    const authority: string = BdAppConfigHandler._getSourceValue('AUTHORITY', source);
    // Optional: dedicated lightweight redirect page for silent token flows.
    // Only emitted when the deployment sets the env var — the URI must be
    // registered in the B2C app registration first (see BdAppConfigResponseMsalConfig).
    const silentRedirectUri = source['AUTHENTICATION_SILENT_REDIRECT_URI'] as string | undefined;

    return {
      ...(silentRedirectUri ? { silentRedirectUri } : {}),
      publicClientApplication: {
        auth: {
          clientId,
          authority: `${authority}/${signUpSignInPolicy}`,
          redirectUri: '/',
          postLogoutRedirectUri: '/',
          knownAuthorities: ['bindocusers.b2clogin.com']
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthInCookie: false
        }
      },
      guardConfig: {
        interactionType: 'redirect',
        authRequest: {
          scopes: [clientId]
        },
        loginFailedRoute: 'sys-login-failed'
      },
      policies: {
        names: {
          signUpSignIn: signUpSignInPolicy
        },
        authorities: {
          signUpSignIn: {
            authority: `${authority}/${signUpSignInPolicy}`
          }
        }
      },
      // One entry per protected key; a key on the bare Modulith host base becomes one entry
      // per protected root of the host (BDA-6595, see hostProtectedResources).
      protectedResources: hostProtectedResources(apiEndpoints, [clientId], source)
    };
  }

  private static _mergeEndpoints(
    apiEndpoints: { [key: string]: string },
    unprotectedApiEndpoints: { [key: string]: string }
  ): { [key: string]: string } {
    const allApiEndpoints: { [key: string]: string } = JSON.parse(JSON.stringify(apiEndpoints));
    Object.keys(unprotectedApiEndpoints).forEach(endpoint => {
      allApiEndpoints[endpoint] = unprotectedApiEndpoints[endpoint];
    });
    return allApiEndpoints;
  }

  private static _getSourceValue<T = string>(
    fieldName: string,
    source: { [key: string]: unknown }
  ): T {
    const value = source[fieldName];

    if (!value) {
      throw new Error(`Source field ${fieldName} not found.`);
    }

    return value as T;
  }
}
